/**
 * Fair Value Gap (FVG)
 *
 * Smart Money Concept: Identifies gaps between candle 1's high and
 * candle 3's low (bullish) or candle 1's low and candle 3's high (bearish).
 * These gaps often act as support/resistance zones.
 *
 * Category: Advanced | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const fvgIndicator: IndicatorDefinition = {
  id: "fair_value_gap",
  name: "Fair Value Gap",
  shortName: "FVG",
  category: "advanced",
  pane: "overlay",
  params: [
    { key: "minGapPct", label: "Min Gap %", type: "number", default: 0.1, min: 0, max: 5, step: 0.05 },
  ],
  plots: [
    { key: "bullFvg", label: "Bull FVG", color: "#4CAF5040", style: "line", lineWidth: 1 },
    { key: "bearFvg", label: "Bear FVG", color: "#F4433640", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { minGapPct } = params;
    const bullData: { time: number; value: number }[] = [];
    const bearData: { time: number; value: number }[] = [];

    for (let i = 2; i < candles.length; i++) {
      const c1 = candles[i - 2];
      const c3 = candles[i];

      // Bullish FVG: c1.high < c3.low (gap up)
      if (c3.low > c1.high) {
        const gapPct = (c3.low - c1.high) / c1.high * 100;
        if (gapPct >= minGapPct) {
          const mid = (c3.low + c1.high) / 2;
          bullData.push({ time: candles[i].time, value: mid });
        }
      }

      // Bearish FVG: c1.low > c3.high (gap down)
      if (c1.low > c3.high) {
        const gapPct = (c1.low - c3.high) / c3.high * 100;
        if (gapPct >= minGapPct) {
          const mid = (c1.low + c3.high) / 2;
          bearData.push({ time: candles[i].time, value: mid });
        }
      }
    }

    return [
      { key: "bullFvg", data: bullData },
      { key: "bearFvg", data: bearData },
    ];
  },
};
